
import { defineComponent } from 'vue'
import { constants } from '@/constants'
import { mapGetters, mapState } from 'vuex'
import ResizeObserver from '@/components/ResizeObserver.vue'
import TutorialModal from '@/components/TutorialModal.vue'
import MessageModal from '@/components/common/MessageModal.vue'
import UserToolbar from '@/components/UserToolbar.vue'
import { AuthActionTypes } from '@/store/modules/auth/interfaces/action-types'
import { LinksActionType } from '@/store/modules/links/interfaces'
import { setI18nLanguage } from '@/i18n'
import Error401Page from './pages/Error401Page.vue'
import BeforeLoginLayout from '@/components/layout/BeforeLoginLayout.vue'
import { LanguagesType } from '@/types'
import { HelpPagesActionType } from '@/store/modules/help-pages/interfaces'
import AppGlobalModal from '@/components/app/AppGlobalModal.vue'
import ModalComponent from './components/ModalComponent.vue'
import { AuthMutationTypes } from '@/store/modules/auth/interfaces/mutation-types'
import { RolePermissionDetails } from './store/modules/users/interfaces/IUser'
import { IAuthState } from './store/modules/auth/IAuthState'
import { IFetchSearchResponse } from '@/store/modules/main/intefaces/fetchSearch/IFetchSearchResponse'
import { IPortalHardCodeLink } from './store/modules/links/interfaces/IPortalHardCodeLink'
import { getValueByLabel } from './utils/permissions'
import AppLayout from '@/components/layout/AppLayout.vue'
import { getUnseenHelpPages } from './utils/newCapabilities'

export default defineComponent({
  name: 'App',

  components: {
    TutorialModal,
    Error401Page,
    MessageModal,
    UserToolbar,
    BeforeLoginLayout,
    ModalComponent,
    AppGlobalModal,
    ResizeObserver,
    AppLayout
  },
  data: () => ({
    showTutorialModal: false,
    gigICode: '',
    gigIvToken: '',
    isPortalChrome: true,
    isLoaded: false,
    openModal: false,
    message: '',
    subMessage: '',
    searchValue: '',
    searchValues: [] as IFetchSearchResponse[],
    isErrorModal: true,
    activeLanguage: (localStorage.getItem('lang')) as LanguagesType,
    clientWidth: 0
  }),

  computed: {
    ...mapState('main', ['pageNotFound']),
    ...mapState('basket', ['id', 'items', 'progressBarMsg']),
    ...mapGetters('main', ['getPermissions']),
    portalHardCodeLink (): IPortalHardCodeLink[] {
      return this.$store.state.links.portalHardCodeLink?.results ?? []
    },
    total () {
      return this.$store.state.links.portalHardCodeLink.total
    },
    portalPageMenuLink () {
      return this.$store.state.links.portalPageMenuLink?.items ?? []
    },
    limit () {
      return this.$store.state.links.portalHardCodeLink.limit
    },
    queries () {
      return this.$store.state.catalog.queries
    },
    catalogPermission () {
      return this.checkPermission('Catalogue', {}).length !== 0
    },

    languages () {
      return ['hu', 'en']
    },

    user () {
      return this.$store.state.auth.user
    },

    isLoggedIn () {
      return this.user.isLoggedIn // && !!this.user.userValidation
    },
    notAuthorized () {
      return this.$route.meta?.requireAuthentication && !this.isLoggedIn
    },
    isPOSUser () {
      return this.user?.permissionSchema?.some((company) => company.rpDetails.some((rp) => rp.customerType === 'POS'))
    },
    isWHSLUser () {
      return this.user?.permissionSchema?.some((company) => company.rpDetails?.some((rp) => rp.customerType === 'WHSL'))
    },
    isOutletUser () {
      return this.user?.permissionSchema?.some((company) =>
        company.rpDetails?.some((rp) => rp.customerType === 'Outlets')
      )
    },
    allPermission () {
      return this.user.allAvailablePermission
    },
    helpPagesStore () {
      console.log(this.$store.state.helpPages)
      return this.$store.state.helpPages
    },
    customerType (): RolePermissionDetails | undefined {
      if (this.user && this.user?.permissionSchema?.length > 0) {
        return this.user?.permissionSchema[0]?.rpDetails?.find((item: RolePermissionDetails) => item.customerType)
      }
      return undefined
    },
    isPOSAcademyUser () {
      return this.user.permissionSchema?.every(permission => permission.rpDetails.every(
        roleItem =>
          roleItem.rolename === 'POS Academy' ||
          roleItem.roleid === 'f5ab4859-9197-47e5-bc9e-097283c35d94'
      ))
    }
  },

  watch: {
    isLoggedIn: {
      handler (value) {
        if (value === true) {
          this.fetchLinks()
          this.fetchPortalHardCodeLink()
          this.fetchPortalPageMenuLink()
        }
      }
    },
    activeLanguage: {
      handler (value) {
        this.setMagentoStore(value)
        if (this.isLoggedIn) {
          this.fetchLinks()
          this.fetchPortalHardCodeLink()
          this.fetchPortalPageMenuLink()
        }
      },
      immediate: true
    },

    $route () {
      this.initFromRoute()
    },

    progressBarMsg (value, oldValue) {
      if (oldValue !== value) {
        this.$store.commit('basket/SET_PROGRESS_BAR', value)
        return value
      }
    }
  },

  created () {
    this.initFromRoute()
  },

  async mounted () {
    this.checkGigyaReady()
  },

  methods: {

    checkGigyaReady () {
      if (window.gigya) {
        window.gigya.hasSession().then(this.checkSession)
        window.gigya.socialize.addEventHandlers({
          onLogin: async (res: IAuthState) => {
            await this.updateUserInfo(res)
            await this.$store.dispatch('basket/FETCH_BASKET_ID', {}, { root: true })
            if (!this.id) {
              await this.$store.dispatch('basket/CREATE_EMPTY_CART', { root: true })
            }
          }
        })
      } else {
        setTimeout(() => {
          this.checkGigyaReady()
        }, 100)
      }
    },

    initFromRoute () {
      this.isPortalChrome = !this.$route.meta?.noPortalChrome
      const urlParams = new URLSearchParams(window.location.search)
      this.gigICode = urlParams.get('gig_i_code') || ''
      this.gigIvToken = urlParams.get('gig_i_vToken') || ''
    },
    checkPermission (permission: string, value: Record<string, any>, additionalPremmision = true) {
      if (this.customerType) localStorage.setItem('customertype', this.customerType?.customerType)

      return this.allPermission?.includes(permission) &&
      additionalPremmision &&
      getValueByLabel(this.getPermissions(), permission)
        ? [value]
        : []
    },
    hasProductRecommendationPermission () {
      const hasPermission = this.allPermission.includes('Product Recommendation')

      if (hasPermission) {
        sessionStorage.setItem('productrecoenabled', '1')
      }

      return hasPermission
    },
    async dispatchHelpPages () {
      await this.$store.dispatch(HelpPagesActionType.FETCH_ALL_HELP_PAGES)
    },
    openTutorialModal () {
      this.showTutorialModal = true
    },
    closeTutorialModal () {
      this.showTutorialModal = false
    },
    changeLanguage (langCode: LanguagesType) {
      this.activeLanguage = langCode
      setI18nLanguage(langCode)
    },

    fetchLinks () {
      this.$store.dispatch(LinksActionType.FETCH_ALL_LINKS)
    },
    fetchPortalHardCodeLink () {
      this.$store.dispatch(LinksActionType.FETCH_PORTAL_HARD_CODE_LINK)
    },
    fetchPortalPageMenuLink () {
      this.$store.dispatch(LinksActionType.FETCH_PAGE_MENU_LINK)
    },

    getLinkByName (name: string) {
      const linkObj = this.portalHardCodeLink.find((link) => link.name === name)
      return linkObj ? { url: linkObj.url, label: linkObj.label } : { url: '', label: '' }
    },

    /**
     * Method to update logged in user info into vuex store
     * @param res
     */
    async updateUserInfo (res: IAuthState) {
      sessionStorage.setItem('uid', res.UID) // This is for Google Analytics
      sessionStorage.setItem('getUserInfoResponse', JSON.stringify(res))
      this.$store.commit('SET_AUTH_USER', res)
      await this.$store.dispatch(AuthActionTypes.GET_USER_INFO, { id: res.UID }).then(() => {
        this.hasProductRecommendationPermission()
        if (localStorage.getItem('lang') !== null) {
          const activeLanguage = (localStorage.getItem('lang') ||
            constants.application.defaulLang) as LanguagesType
          this.changeLanguage(activeLanguage)
        }
        if (!this.user.userValidation && !this.$route.meta?.isUserVerification) {
          this.userLogout()
          return
        }

        this.setMagentoStore(this.activeLanguage)

        this.fetchLinks()
      })

      if (!!this.user.userValidation && !this.$route.meta?.isUserVerification) {
        await this.checkPOSAcademyUser()
      }

      if (
        !(this.helpPagesStore !== undefined && this.helpPagesStore.helpPages && this.helpPagesStore.helpPages.length)
      ) {
        await this.dispatchHelpPages()
        if (((this.user.showTutorial > 0 || this.user.showTutorial === null) || getUnseenHelpPages(this.helpPagesStore.helpPages.filter(h => h?.newCapability === true), this.user.UID).length > 0)) {
          this.openTutorialModal()
        }
      }
    },
    checkPOSAcademyUser () {
      if (this.isPOSAcademyUser) {
        window.gigya.fidm.saml.initSSO({
          spName: this.user.country === 'SVK' ? 'skELearning' : 'eLearning',
          redirectURL: this.user.country === 'SVK' ? this.getLinkByName('skELearning').url : this.getLinkByName('eLearning').url
        })
      }
    },
    /**
     * Method to initiate logout action
     * on click action of logout button
     */
    userLogout () {
      sessionStorage.removeItem('uid')
      sessionStorage.removeItem('imp_usr')
      sessionStorage.removeItem('isFromFavoritePage')
      const params = {
        UID: this.user.UID,
        callback: this.handleLogout
      }
      window.gigya.accounts.logout(params)
    },
    /**
     * Method to perform action after user logs out
     * Navigate user to home page
     */
    handleLogout () {
      this.$store.dispatch('logout')
      this.$navigate('/login')
    },
    /**
     * Method to check user session if it exist
     * if it does not exist then redirect user to login page
     * @param sessionExist
     */
    async checkSession (sessionExist: boolean) {
      try {
        if (sessionExist) {
          window.gigya.socialize.getUserInfo({ callback: this.updateUserInfo })
        } else {
          if (!this.$route.meta?.isUserVerification && this.$route.meta?.requireAuthentication) {
            this.$navigate('/login')
          }
        }
      } catch (error) {
        console.error('Error in checkSession:', error)
      } finally {
        this.isLoaded = true
      }
    },

    setMagentoStore (language: string) {
      localStorage.setItem('store', `LivePortal_HU_${language}`)
    },

    onResizePortal ({ height }: { height: number }) {
      this.clientWidth = document.querySelector('body')?.clientWidth || height
    }
  }
})
